import axios from 'axios';
import type {
  AthleteProfile,
  BroadJumpScatterPoint,
  LeaderboardQuery,
  LeaderboardRow,
  MetricAverage,
  PublicResult,
  SprintChartData,
} from '../types/result';
import {
  buildComparisons,
  getShortAthleteId,
  primaryMetricKeys,
} from '../lib/result_utils';
import { resultRepository as mockResultRepository } from '../lib/mock_result_repository';
import {
  deriveWebSocketUrl,
  isIngestionJobStatus,
  type IngestionJobStatus,
} from './ingestion';

interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

interface ConfirmAthleteData {
  athleteId: string;
  externalIdNoWithoutText: string;
}

interface ApiAthleteResult {
  externalId: string;
  externalIdNoWithoutText: string;
  eventName: string;
  eventDate: string;
  firstName: string;
  lastName: string | null;
  isProfessional: boolean;
  age: number | null;
  athletePosition: string | null;
  heightInches: number | null;
  weightPounds: number | null;
  reachInches: number | null;
  sprint10Seconds: number | null;
  sprint20Seconds: number | null;
  sprint30Seconds: number | null;
  sprint40Seconds: number | null;
  threeConeSeconds: number | null;
  verticalJumpInches?: number | null;
  shuttle20Seconds?: number | null;
  broadJumpInches: number | null;
  powerIndex: number | null;
  overallScore: number | null;
  throwingAccuracy: number | null;
  throwingPower: number | null;
  fullSizeImageUrl: string | null;
  thumbnailImageUrl: string | null;
  socialCard4x5Url: string | null;
  socialCard9x16Url: string | null;
  socialCard4x5DownloadUrl: string | null;
  socialCard9x16DownloadUrl: string | null;
}

interface ApiMetricAverages {
  sprint40Seconds: number | null;
  threeConeSeconds: number | null;
  verticalJumpInches?: number | null;
  shuttle20Seconds?: number | null;
  broadJumpInches: number | null;
  throwingAccuracy: number | null;
  throwingPower: number | null;
}

interface ApiAthleteProfile {
  athlete: ApiAthleteResult;
  rank: number | null;
  participantCount: number;
  participantAverages: ApiMetricAverages;
  professionalBenchmarks: ApiAthleteResult[];
  broadJumpScatterPoints: BroadJumpScatterPoint[];
  sprintChart: SprintChartData;
}

type ApiLeaderboardRow = Omit<LeaderboardRow, 'proAgilitySeconds'> & {
  shuttle20Seconds: number | null;
};

const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();

const client = axios.create({
  baseURL: configuredBaseUrl || 'http://localhost:3000/api',
  timeout: 10_000,
});

export function getWebSocketUrl(): string {
  return deriveWebSocketUrl(
    import.meta.env.VITE_WEBSOCKET_URL,
    import.meta.env.VITE_API_BASE_URL,
  );
}

function isNotFound(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    (error.response?.status === 400 || error.response?.status === 404)
  );
}

function toPublicResult(result: ApiAthleteResult): PublicResult {
  return {
    externalId: result.externalId,
    eventName: result.eventName,
    eventDate: result.eventDate,
    firstName: result.firstName,
    lastName: result.lastName,
    age: result.age,
    isProfessional: result.isProfessional,
    benchmarkLabel: null,
    athletePosition: result.athletePosition,
    heightInches: result.heightInches,
    weightPounds: result.weightPounds,
    reachInches: result.reachInches,
    sprint10Seconds: result.sprint10Seconds,
    sprint20Seconds: result.sprint20Seconds,
    sprint30Seconds: result.sprint30Seconds,
    sprint40Seconds: result.sprint40Seconds,
    threeConeSeconds: result.threeConeSeconds,
    verticalJumpInches: result.verticalJumpInches ?? null,
    proAgilitySeconds: result.shuttle20Seconds ?? null,
    broadJumpInches: result.broadJumpInches,
    powerIndex: result.powerIndex,
    overallScore: result.overallScore,
    throwingAccuracy: result.throwingAccuracy,
    throwingPower: result.throwingPower,
    fullSizeImageUrl: result.fullSizeImageUrl,
    thumbnailImageUrl: result.thumbnailImageUrl,
    socialCard4x5Url: result.socialCard4x5Url,
    socialCard9x16Url: result.socialCard9x16Url,
    socialCard4x5DownloadUrl: result.socialCard4x5DownloadUrl,
    socialCard9x16DownloadUrl: result.socialCard9x16DownloadUrl,
    mockMetricKeys: [],
  };
}

function toMetricAverages(averages: ApiMetricAverages): MetricAverage[] {
  return primaryMetricKeys.map((key) => ({
    key,
    value:
      key === 'proAgilitySeconds'
        ? (averages.shuttle20Seconds ?? null)
        : (averages[key] ?? null),
  }));
}

async function confirmAthleteIdFromApi(
  athleteId: string,
): Promise<ConfirmAthleteData | null> {
  try {
    const response = await client.get<ApiResponse<ConfirmAthleteData>>(
      `public/athletes/${encodeURIComponent(athleteId)}/confirm`,
    );
    return response.data.data;
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
}

async function getAthleteProfileFromApi(
  athleteId: string,
): Promise<AthleteProfile | null> {
  try {
    const requestAthleteId = getShortAthleteId(athleteId);
    const response = await client.get<ApiResponse<ApiAthleteProfile>>(
      `public/athletes/${encodeURIComponent(requestAthleteId)}`,
    );
    const data = response.data.data;
    const athlete = toPublicResult(data.athlete);
    const professionalBenchmarks = data.professionalBenchmarks.map(toPublicResult);

    return {
      athlete,
      rank: data.rank,
      participantCount: data.participantCount,
      participantAverages: toMetricAverages(data.participantAverages),
      professionalBenchmarks,
      comparisons: buildComparisons(athlete, professionalBenchmarks),
      broadJumpScatterPoints: data.broadJumpScatterPoints,
      sprintChart: data.sprintChart,
    };
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
}

async function getLeaderboardFromApi(
  query: LeaderboardQuery = {},
): Promise<LeaderboardRow[]> {
  const search = query.search?.trim();
  const response = await client.get<ApiResponse<ApiLeaderboardRow[]>>(
    'public/leaderboard',
    {
      params: search ? { search } : undefined,
    },
  );

  return response.data.data.map(({ shuttle20Seconds, ...row }) => ({
    ...row,
    proAgilitySeconds: shuttle20Seconds,
  }));
}

const axiosPublicApi = {
  confirmAthleteId: confirmAthleteIdFromApi,
  getAthleteProfile: getAthleteProfileFromApi,
  getLeaderboard: getLeaderboardFromApi,
};

const mockPublicApi = {
  async confirmAthleteId(athleteId: string): Promise<ConfirmAthleteData | null> {
    const athlete = await mockResultRepository.findByExternalId(athleteId);
    if (!athlete) {
      return null;
    }

    return {
      athleteId: athlete.externalId,
      externalIdNoWithoutText: getShortAthleteId(athlete.externalId),
    };
  },
  getAthleteProfile: (athleteId: string) =>
    mockResultRepository.getAthleteProfile(athleteId),
  getLeaderboard: (query: LeaderboardQuery = {}) =>
    mockResultRepository.listLeaderboard(query),
};

const dataSource = import.meta.env.VITE_DATA_SOURCE?.trim().toLowerCase();

export const publicApi = dataSource === 'mock' ? mockPublicApi : axiosPublicApi;



// Admin API

export interface GetUploadUrlsResponse {
  jobId: string;
  csv: {
    key: string;
    url: string;
  };
  zip: {
    key: string;
    url: string;
  };
}



export async function apiAdminGetUploadUrls(accessToken: string): Promise<GetUploadUrlsResponse> {
  const response = await client.get<ApiResponse<GetUploadUrlsResponse>>(
    'admin/upload-data',
    {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    },
  );
  // Validate the response structure
  if (
    !response.data ||
    typeof response.data !== 'object' ||
    !response.data.data ||
    typeof response.data.data !== 'object' ||
    typeof response.data.data.jobId !== 'string' ||
    !response.data.data.csv ||
    typeof response.data.data.csv.key !== 'string' ||
    typeof response.data.data.csv.url !== 'string' ||
    !response.data.data.zip ||
    typeof response.data.data.zip.key !== 'string' ||
    typeof response.data.data.zip.url !== 'string'
  ) {
    throw new Error('Invalid API response structure');
  }

  return response.data.data;
}

export async function apiAdminUploadTriggerIngestion(
  jobId: string,
  accessToken: string,
): Promise<void> {
  await client.post('admin/upload-data/trigger-ingestion', {
    jobId: jobId,
  }, {
    headers: {
      Authorization: 'Bearer ' + accessToken,
    },
  });
}

export async function apiAdminGetIngestionStatus(
  jobId: string,
  accessToken: string,
): Promise<IngestionJobStatus> {
  const response = await client.get<ApiResponse<IngestionJobStatus>>(
    `admin/ingestion/${encodeURIComponent(jobId)}/status`,
    {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    },
  );

  const payload = response.data?.data;
  if (!isIngestionJobStatus(payload) || payload.id !== jobId) {
    throw new Error('Invalid ingestion status response');
  }

  return payload;
}

export type AdminDeletionReason = 'PRIVACY' | 'INCORRECT_DATA';

export interface AdminResult {
  id: string;
  externalId: string;
  externalIdNoWithoutText: string;
  eventName: string;
  eventDate: string;
  firstName: string;
  lastName: string | null;
  isProfessional: boolean;
  rank: number | null;
  overallScore: number | null;
  sprint40Seconds: number | null;
  broadJumpInches: number | null;
  verticalJumpInches: number | null;
  proAgilitySeconds: number | null;
  thumbnailImageUrl: string | null;
  assetStatus: 'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED';
  deletionStatus: 'ACTIVE' | 'DELETING';
  updatedAt: string;
}

export interface AdminResultsPage {
  items: AdminResult[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export async function apiAdminGetResults(
  accessToken: string,
  query: { search?: string; page?: number; pageSize?: number } = {},
): Promise<AdminResultsPage> {
  const response = await client.get<ApiResponse<AdminResultsPage>>(
    'admin/results',
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: query,
    },
  );
  return response.data.data;
}

export async function apiAdminDeleteResult(
  resultId: string,
  reason: AdminDeletionReason,
  accessToken: string,
): Promise<void> {
  await client.delete(`admin/results/${encodeURIComponent(resultId)}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: { reason },
  });
}

export function getAdminApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
  }
  return fallback;
}
